/**
 * The "last seen" reader (spec §2.1, card C0). No new save field: station market, board (the system's faction
 * requests) and economy data in flight all come from one system view snapshot, and the career price log
 * (`career.priceLog`, TR-1b, D-487) already stamps every snapshot the pilot took with career game time `t` (on system
 * entry, on each dock and, for covered systems, daily). A covered system (D-494) reads live. The stamp is per
 * system: every station in it shares the age.
 */
import type { DashboardConfig } from '../config'
import { ageStamp, type Age } from '../parts'
import type { SeenKind, SeenSource, SeenStamp } from './types'

const NEVER: SeenStamp = { at: null, live: false }

/**
 * When the pilot last saw `kind` data for `systemId` (`kind` is kept for C3/C5 call sites: all three kinds share the
 * snapshot today). Live systems stamp `now`.
 */
export function lastSeen(src: SeenSource, systemId: string, _kind: SeenKind, now: number): SeenStamp {
  if (src.covered.has(systemId)) return { at: now, live: true }
  const entry = src.priceLog?.[systemId]
  return entry === undefined ? NEVER : { at: entry.t, live: false }
}

/**
 * The `AgeStamp` line for a station's data: "Here · live" docked there, else the system's last-seen age (fresh,
 * aging, stale over `staleDays`, or never).
 */
export function stationAge(
  cfg: DashboardConfig['age'],
  src: SeenSource,
  station: { readonly id: string; readonly name: string; readonly systemId: string },
  kind: SeenKind,
  now: number,
  dockedStationId: string | null,
): Age {
  const seen = lastSeen(src, station.systemId, kind, now)
  return ageStamp(cfg, station.name, seen.at, now, dockedStationId === station.id)
}
